import { QuestionMarkCircleIcon } from '@heroicons/react/20/solid'

import { classNames } from '../../helper'

// Erklärungen für Leute, die nicht täglich A/B-Tests fahren. Fachbegriffe
// bleiben stehen und werden erklärt, damit man sie mit der Zeit lernt.

// Aufklappbare Erklärung unter einem Feld oder Abschnitt. Zu, solange niemand
// klickt, damit das Formular kompakt bleibt.
export const Explain = ({ title = 'Was heißt das?', children, className }) => (
  <details className={classNames('group text-xs text-gray-600', className)}>
    <summary className='inline-flex cursor-pointer select-none items-center gap-1 font-medium text-indigo-600 hover:text-indigo-500'>
      <QuestionMarkCircleIcon className='h-4 w-4' aria-hidden='true' />
      {title}
    </summary>
    <div className='mt-2 space-y-2 rounded-md bg-indigo-50/60 px-3 py-2 leading-relaxed text-gray-700 ring-1 ring-inset ring-indigo-100'>
      {children}
    </div>
  </details>
)

// Begriff fett, Erklärung dahinter. Für Listen in Explain.
export const Term = ({ name, children }) => (
  <p>
    <span className='font-semibold text-gray-900'>{name}</span> {children}
  </p>
)

// Auswirkung einer Einstellung, optisch abgesetzt.
export const Effect = ({ children }) => (
  <p className='border-l-2 border-amber-400 pl-2 text-gray-700'>
    <span className='font-semibold'>Auswirkung</span> {children}
  </p>
)

export const HELP = {
  key: (
    <>
      <Term name='Key'>
        ist der technische Name des Flags. Über ihn fragt der Code die Variante
        ab, z. B. useVariant(&apos;funnel_email_position&apos;).
      </Term>
      <Effect>
        Ändert sich der Key, findet der Code das Flag nicht mehr. Deshalb ist er
        nach dem Anlegen fest. Am besten sprechend benennen, also Ort plus Idee.
      </Effect>
    </>
  ),
  kind: (
    <>
      <Term name='Rollout'>
        schaltet ein Feature für einen Prozentsatz der Nutzer ein. Es gibt nur
        die Varianten on und off. Gedacht für vorsichtiges Ausrollen, z. B. erst
        10 %, dann 50 %, dann alle. Es wird nichts statistisch ausgewertet.
      </Term>
      <Term name='Experiment'>
        (A/B-Test) teilt Besucher zufällig auf zwei oder mehr Varianten auf und
        misst, welche besser funktioniert. Der Evaluator wertet täglich aus und
        beendet den Test automatisch, wenn die Kriterien erfüllt sind.
      </Term>
      <Effect>
        Ab dem Start ist die Art fest. Wer von Rollout auf Experiment wechseln
        will, legt ein neues Flag an.
      </Effect>
    </>
  ),
  unit: (
    <>
      <p>
        Die Unit legt fest, wer als eine Person zählt. Alle mit derselben Unit
        sehen immer dieselbe Variante.
      </p>
      <Term name='visitor'>
        ist ein Browser bzw. Besucher, erkannt über die Besucher-ID (aivy_vid).
        Richtig für Landingpage und Funnel, wo noch niemand eingeloggt ist.
      </Term>
      <Term name='partner'>
        ist ein Kundenkonto. Alle Nutzer einer Firma sehen dieselbe Variante.
        Richtig für Features im Dashboard, bei denen ein Team nicht
        unterschiedliche Versionen sehen soll.
      </Term>
      <Term name='user'>
        ist ein einzelner eingeloggter Nutzer. Kollegen derselben Firma können
        unterschiedliche Varianten sehen.
      </Term>
      <Effect>
        Nur Experimente mit Unit visitor wertet der Evaluator automatisch aus.
        Bei partner und user schaust du die Zahlen selbst an.
      </Effect>
    </>
  ),
  surface: (
    <>
      <p>Die Surface ist der Ort, an dem das Flag wirkt.</p>
      <Term name='lp'>
        ist die Landingpage landingpage.aivy.app. Sie teilt zu und gibt die
        Variante an den Funnel weiter.
      </Term>
      <Term name='funnel'>
        ist der Funnel /funnel, in dem Leads ihre E-Mail und vier Fragen
        beantworten.
      </Term>
      <Term name='dashboard'>ist das Kunden-Dashboard nach dem Login.</Term>
      <Effect>
        Die Surface allein ändert nichts. Im Code der jeweiligen Seite muss die
        Variante abgefragt und umgesetzt sein, sonst sehen alle dasselbe.
      </Effect>
    </>
  ),
  salt: (
    <>
      <Term name='Salt'>
        ist ein Zusatz zur Zufallsverteilung. Aus Besucher-ID und Salt wird
        berechnet, welche Variante jemand bekommt. Gleicher Salt heißt gleiche
        Verteilung, auch über Tage hinweg.
      </Term>
      <Effect>
        Ein neuer Salt mischt alle neu. Das braucht man beim Neustart eines
        Tests, damit nicht dieselben Leute wieder in derselben Gruppe landen. Im
        Normalfall einfach den Key stehen lassen.
      </Effect>
    </>
  ),
  variants: (
    <>
      <Term name='Control'>
        ist die erste Variante und immer der heutige Stand. Gegen sie wird
        verglichen. Sie bekommt auch jeder, der nicht im Test ist.
      </Term>
      <Term name='Gewicht'>
        bestimmt den Anteil. 50 und 50 heißt halbe-halbe, 80 und 20 heißt 80 %
        Control. Die Zahlen müssen nicht 100 ergeben, sie werden umgerechnet.
      </Term>
      <Effect>
        50/50 kommt am schnellsten zu einem Ergebnis. Ungleiche Gewichte nur,
        wenn die neue Variante riskant ist. Ab dem Start sind Varianten und
        Gewichte fest, weil sich sonst die Gruppen verschieben würden.
      </Effect>
    </>
  ),
  percent: (
    <>
      <p>Der Prozentsatz der Units, die das Feature bekommen (Variante on).</p>
      <Effect>
        Hochdrehen behält alle, die schon drin sind, es kommen nur neue dazu.
        Runterdrehen nimmt es Leuten wieder weg. 100 % heißt alle, 0 % heißt
        niemand. Änderungen wirken beim nächsten Seitenaufruf.
      </Effect>
    </>
  ),
  targeting: (
    <>
      <p>
        Targeting schränkt ein, wer überhaupt am Flag teilnimmt. Wer nicht
        passt, bekommt Control bzw. off und zählt nicht als Exposure.
      </p>
      <Term name='Stages'>
        sind die Umgebungen develop, staging und production. Leer heißt überall.
        Zum Testen erst nur develop oder staging anhaken.
      </Term>
      <Term name='Lead-Ampel'>
        ist die Einstufung aus dem Funnel (GRUEN, GELB, ROT).
      </Term>
      <Term name='Partner-IDs'>
        begrenzen auf bestimmte Kundenkonten, z. B. für einen Pilotkunden.
      </Term>
      <Term name='Trial'>unterscheidet Testkonten von zahlenden Kunden.</Term>
      <Effect>
        Lead-Ampel, Partner-IDs und Trial kennt das System nur bei eingeloggten
        Nutzern im Dashboard. Auf Landingpage und im Funnel sind sie unbekannt,
        dort würde dann niemand teilnehmen.
      </Effect>
    </>
  ),
  overrides: (
    <>
      <Term name='Override'>
        gibt einer bestimmten Unit fest eine Variante, unabhängig vom Zufall.
        Typisch für QA oder eine Demo beim Kunden.
      </Term>
      <Effect>
        Overrides zählen nie als Exposure und verfälschen das Ergebnis nicht.
        Für einen schnellen Blick geht auch der URL-Parameter ff_&lt;key&gt;=
        &lt;variante&gt;, z. B. /funnel?ff_funnel_email_position=email_last.
      </Effect>
    </>
  ),
  decision: (
    <>
      <p>
        Hier legst du vor dem Start fest, wann der Test entschieden ist. Danach
        ist alles gesperrt. Das ist Absicht, denn wer Kriterien während des
        Tests anpasst, findet fast immer irgendeinen Gewinner, auch wenn es
        keinen echten Unterschied gibt.
      </p>
      <Term name='Exposure'>
        heißt, jemand hat eine Variante tatsächlich gesehen. Das ist der Nenner
        für alle Raten.
      </Term>
      <Term name='Conversion'>
        heißt, jemand hat das Ziel erreicht, z. B. einen Lead abgeschickt.
      </Term>
    </>
  ),
  primary: (
    <>
      <p>Die primäre Metrik entscheidet über den Gewinner.</p>
      <Term name='Funnel gestartet (funnel_started)'>
        zählt, wer den Funnel öffnet. Sinnvoll für Tests auf der Landingpage.
      </Term>
      <Term name='Lead eingegangen (lead_received)'>
        zählt abgeschickte Funnels, also Status RECEIVED oder weiter. Der
        Standard für Funnel-Tests.
      </Term>
      <Term name='Account angelegt (account_created)'>
        zählt, für wen ein Dashboard-Account entstanden ist.
      </Term>
      <Term name='Eingeloggt (logged_in)'>
        zählt, wer sich danach wirklich eingeloggt hat. Am aussagekräftigsten,
        braucht aber am meisten Traffic.
      </Term>
      <Effect>
        Je weiter hinten die Metrik, desto seltener ist sie und desto länger
        dauert der Test. Die anderen Metriken siehst du trotzdem im Ergebnis.
      </Effect>
    </>
  ),
  minDays: (
    <>
      <Term name='min_days'>
        ist die Mindestlaufzeit. Vorher entscheidet der Evaluator nie, auch wenn
        die Zahlen schon eindeutig aussehen.
      </Term>
      <Effect>
        14 Tage decken zwei volle Wochen ab, also Wochentage und Wochenenden.
        Kürzer lohnt nur zum Ausprobieren auf staging.
      </Effect>
    </>
  ),
  maxDays: (
    <>
      <Term name='max_days'>
        ist die maximale Laufzeit. Gibt es bis dahin keinen klaren Gewinner,
        endet der Test mit dem Ergebnis kein Effekt und alle bekommen Control.
      </Term>
      <Effect>
        Verhindert, dass ein Test ewig läuft. Eine Pause hält die Uhr nicht an.
      </Effect>
    </>
  ),
  minExposures: (
    <>
      <Term name='min_exposures_per_variant'>
        ist die Mindestzahl an Besuchern je Variante, bevor entschieden wird.
      </Term>
      <Effect>
        Schützt vor Zufallstreffern bei wenigen Besuchern. Bei 20 Besuchern
        können zwei Leads mehr schon nach einem riesigen Unterschied aussehen.
      </Effect>
    </>
  ),
  minConversions: (
    <>
      <Term name='min_conversions_total'>
        ist die Mindestzahl an Conversions über alle Varianten zusammen.
      </Term>
      <Effect>
        Ohne genug Conversions gibt es nichts zu vergleichen. Bei seltenen
        Zielen wie Leads ist das oft die Hürde, die am längsten dauert.
      </Effect>
    </>
  ),
  threshold: (
    <>
      <Term name='P(best)'>
        ist die Wahrscheinlichkeit, dass eine Variante wirklich die beste ist,
        berechnet aus den bisherigen Zahlen.
      </Term>
      <Term name='threshold'>
        ist die Schwelle, ab der eine Variante gewinnt. 0,99 heißt, sie muss zu
        99 % die beste sein.
      </Term>
      <Effect>
        Der Evaluator schaut jeden Tag. Jeder Blick ist eine neue Chance auf
        einen Zufallsgewinner. Mit 0,95 gäbe es bei gleich guten Varianten in
        rund einem Drittel der Tests einen falschen Gewinner, mit 0,99 in rund
        einem Zehntel. Deshalb 0,99 als Standard.
      </Effect>
    </>
  ),
  estimator: (
    <>
      <p>
        Der Laufzeitschätzer rechnet aus, wie viele Besucher und Tage der Test
        ungefähr braucht. Er speichert nichts und ändert nichts am Flag.
      </p>
      <Term name='Baseline-Conversionrate'>
        ist die heutige Rate bei Control, z. B. 5 % der Funnel-Starts werden zum
        Lead.
      </Term>
      <Term name='Minimal relevanter Effekt'>
        ist die kleinste Verbesserung, die dich interessiert, relativ zur
        Baseline. 50 heißt, aus 5 % sollen mindestens 7,5 % werden.
      </Term>
      <Term name='Täglicher Traffic'>
        ist die Zahl der Besucher pro Tag, die überhaupt in den Test kommen.
      </Term>
      <Effect>
        Kleine Effekte brauchen sehr viel Traffic. Liegt die Schätzung über
        max_days, endet der Test voraussichtlich ohne Gewinner. Dann lieber eine
        mutigere Änderung testen oder eine häufigere Metrik wählen.
      </Effect>
    </>
  ),
  results: (
    <>
      <Term name='Exposures'>
        sind die Besucher, die eine Variante gesehen haben. Jeder zählt einmal.
      </Term>
      <Term name='Conversions'>
        stehen je Metrik in den Spalten. Die primäre Metrik ist hervorgehoben.
      </Term>
      <Term name='Rate primär'>
        ist Conversions der primären Metrik geteilt durch Exposures.
      </Term>
      <Term name='P(best)'>
        ist die Wahrscheinlichkeit, dass diese Variante die beste ist. Die Werte
        aller Varianten ergeben zusammen 100 %.
      </Term>
      <Effect>
        Am Anfang schwanken die Zahlen stark. Nicht vorzeitig von Hand beenden,
        der Evaluator entscheidet nach den festgelegten Kriterien.
      </Effect>
    </>
  ),
  decisionStates: (
    <>
      <Term name='Sammelt Daten'>
        heißt, eine Mindestgrenze ist noch nicht erreicht oder keine Variante
        liegt über der Schwelle. Einfach weiterlaufen lassen.
      </Term>
      <Term name='Gewinner steht fest'>
        heißt, eine Variante hat die Schwelle erreicht. Der Evaluator beendet
        den Test und meldet sich in Slack.
      </Term>
      <Term name='Kein Effekt'>
        heißt, max_days ist erreicht ohne klaren Gewinner. Alle bekommen
        Control. Das ist auch ein Ergebnis, die Idee bringt nichts Messbares.
      </Term>
      <Term name='Verteilung auffällig (SRM)'>
        heißt Sample Ratio Mismatch. Die Besucher sind nicht so auf die
        Varianten verteilt, wie die Gewichte es vorgeben, z. B. 70/30 statt
        50/50. Fast immer ein technischer Fehler, etwa wenn eine Variante
        Besucher verliert oder nicht mitgezählt wird. Bitte Entwickler
        informieren, bis dahin wird nichts entschieden.
      </Term>
      <Term name='Auswertung von Hand'>
        heißt, der Evaluator rechnet hier nicht automatisch, z. B. weil die Unit
        nicht visitor ist.
      </Term>
    </>
  )
}

// Kurzerklärung als Tooltip auf den Badges.
export const STATUS_HINTS = {
  draft: 'Entwurf. Noch nicht aktiv, alle sehen den heutigen Stand.',
  running: 'Läuft. Besucher werden zugeteilt und gezählt.',
  paused:
    'Pausiert. Alle bekommen Control bzw. off, es wird nichts gezählt. Die Laufzeit zählt weiter.',
  concluded: 'Beendet. Alle bekommen den Gewinner.'
}

export const DECISION_HINTS = {
  collecting: 'Noch nicht genug Daten oder kein klarer Gewinner.',
  winner: 'Eine Variante hat die Schwelle erreicht.',
  no_effect: 'max_days erreicht ohne klaren Gewinner, es bleibt Control.',
  srm: 'Sample Ratio Mismatch. Die Verteilung passt nicht zu den Gewichten, bitte Entwickler informieren.',
  manual: 'Der Evaluator wertet hier nicht automatisch aus.'
}

export const ACTIONS_HELP = (
  <>
    <Term name='Starten'>
      beginnt mit der Zuteilung. Ab dann zählt jede Zuteilung als Exposure, und
      Varianten, Gewichte, Salt und Kriterien sind gesperrt. Am nächsten Morgen
      kommt die erste Auswertung, in Slack eine Startmeldung.
    </Term>
    <Term name='Pausieren'>
      stoppt den Test vorübergehend. Alle bekommen Control bzw. off. Die
      Laufzeit zählt weiter, eine lange Pause kann deshalb max_days aufbrauchen.
    </Term>
    <Term name='Wieder aufnehmen'>
      setzt den Test mit derselben Verteilung fort.
    </Term>
    <Term name='Beenden'>
      schließt von Hand ab und legt fest, welche Variante alle bekommen. Bei
      Experimenten macht das normalerweise der Evaluator, von Hand nur, wenn
      etwas schiefläuft.
    </Term>
    <Term name='Neu starten'>
      beginnt einen beendeten Test von vorn, mit neuem Salt und neuer Laufzeit.
      Die alten Zahlen zählen nicht mit.
    </Term>
    <Term name='Bearbeiten'>
      ändert Beschreibung, Targeting und Overrides jederzeit. Gesperrte Felder
      sind ausgegraut.
    </Term>
  </>
)

// Kurzanleitung oben auf der Übersicht.
export const Guide = () => (
  <Explain title='So funktionieren Experimente und Rollouts' className='mt-3'>
    <Term name='Rollout'>
      schaltet ein Feature für x % der Nutzer ein, ohne neuen Deploy. Gut zum
      vorsichtigen Ausrollen.
    </Term>
    <Term name='Experiment'>
      (A/B-Test) vergleicht Varianten an zufällig verteilten Besuchern und
      entscheidet anhand vorab festgelegter Kriterien.
    </Term>
    <p className='font-semibold text-gray-900'>Ablauf eines Experiments</p>
    <ol className='list-inside list-decimal space-y-1'>
      <li>
        Entwickler bauen die Varianten in den Code, das Flag steht solange auf
        Entwurf. Im Entwurf sieht niemand etwas Neues.
      </li>
      <li>
        Flag anlegen, Kriterien festlegen und mit dem Laufzeitschätzer prüfen,
        ob der Traffic reicht.
      </li>
      <li>
        Mit dem Override oder ?ff_&lt;key&gt;=&lt;variante&gt; beide Varianten
        selbst anschauen.
      </li>
      <li>
        Starten. Ab dann wird zugeteilt, Varianten und Kriterien sind gesperrt.
      </li>
      <li>
        Der Evaluator rechnet jeden Morgen gegen 07:00 UTC und beendet den Test
        automatisch. Das Ergebnis kommt in Slack.
      </li>
      <li>
        Danach entfernen Entwickler die Verlierer-Variante aus dem Code. Das
        Flag bleibt als Beendet stehen, damit man später nachsehen kann.
      </li>
    </ol>
    <p className='font-semibold text-gray-900'>Spalten der Übersicht</p>
    <Term name='Surface'>ist der Ort, an dem das Flag wirkt.</Term>
    <Term name='Unit'>ist das, was als eine Person zählt.</Term>
    <Term name='Verteilung'>
      zeigt die Anteile der Varianten bzw. den Prozentsatz beim Rollout.
    </Term>
    <Term name='Stand'>
      zeigt das letzte Ergebnis des Evaluators, je Variante Exposures /
      Conversions (Rate) und P(best).
    </Term>
  </Explain>
)
